import React, { useState, useEffect, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Alert,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  StatusBar,
  Platform,
  FlatList,
  Image,
  KeyboardAvoidingView,
  Animated,
  Switch,
  Share,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import * as Linking from 'expo-linking';
import axios from 'axios';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

const { width, height } = Dimensions.get('window');

// AI Configuration
const AI_CONFIG = {
  OPENAI_API_KEY: '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',
  OPENROUTER_API_KEY: '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',
  HUGGINGFACE_API_KEY: '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',
  PRIMARY_PROVIDER: 'openai',
};

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (e) {}
  return config;
}, (e) => Promise.reject(e));

const getBusinessId = async () => {
  let id = await AsyncStorage.getItem('company_id');
  if (!id) id = await AsyncStorage.getItem('company_id');
  if (!id) {
    try {
      const p = await AsyncStorage.getItem('company_profile');
      if (p) { const parsed = JSON.parse(p); id = parsed?.id ? String(parsed.id) : null; }
    } catch {}
  }
  return id || null;
};

export default function WebsiteManagementScreen({ navigation, route }) {
  // Company profile state
  const [companyId, setCompanyId] = useState(null);

  const { ask: askAI, agents: aiAgents, loading: aiLoading } = useAI('WebsiteManagementScreen');
  const [showAIChat, setShowAIChat] = useState(false);

  const [companyProfile, setCompanyProfile] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scaleAnim = useRef(new Animated.Value(0.95)).current;

  // Core state
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [syncStatus, setSyncStatus] = useState('connected');
  
  // AI Features State
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiContentType, setAiContentType] = useState('news');
  const [apiKeys, setApiKeys] = useState({
    openai: AI_CONFIG.OPENAI_API_KEY,
    openrouter: AI_CONFIG.OPENROUTER_API_KEY,
    huggingface: AI_CONFIG.HUGGINGFACE_API_KEY
  });
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  
  // Website data
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [websiteContent, setWebsiteContent] = useState({
    news: [],
    advertisements: [],
    pages: [],
    services: [],
    products: [],
  });
  const [analytics, setAnalytics] = useState({
    totalVisitors: 0,
    pageViews: 0,
    bounceRate: 0,
    avgSessionDuration: 0,
    topPages: [],
    recentActivity: [],
  });
  
  // UI state
  const [showWebsiteModal, setShowWebsiteModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showBuilderModal, setShowBuilderModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [contentType, setContentType] = useState('news');
  const [editingContent, setEditingContent] = useState(null);
  
  // Preview states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewType, setPreviewType] = useState('website');
  
  // Form states
  const [websiteForm, setWebsiteForm] = useState({
    id: '',
    name: '',
    domain: '',
    template: 'business',
    title: '',
    description: '',
    logo: '',
    favicon: '',
    colors: {
      primary: '#667eea',
      secondary: '#764ba2',
      accent: '#10B981',
      background: '#FFFFFF',
      text: '#1C1C1E',
    },
    contact: {
      email: companyProfile?.email || '',
      phone: companyProfile?.phone || '',
      address: companyProfile?.headquarters || companyProfile?.address || '',
      website: companyProfile?.website || '',
    },
    social: {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: '',
      youtube: '',
    },
    features: {
      blog: true,
      ecommerce: false,
      booking: false,
      gallery: true,
      testimonials: true,
      newsletter: false,
    },
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: '',
      googleAnalytics: '',
      facebookPixel: '',
    },
    isPublished: false,
    publishedAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const [contentForm, setContentForm] = useState({
    id: '',
    type: 'news',
    title: '',
    content: '',
    excerpt: '',
    featuredImage: '',
    gallery: [],
    category: '',
    tags: [],
    author: '',
    publishDate: new Date().toISOString(),
    isPublished: false,
    isFeatured: false,
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: '',
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  // Templates data
  const websiteTemplates = [
    {
      id: 'business',
      name: 'Business Professional',
      description: 'Perfect for corporate websites and professional services',
      image: 'https://via.placeholder.com/300x200/667eea/FFFFFF?text=Business',
      features: ['Contact Forms', 'Service Pages', 'Team Section', 'Testimonials'],
      color: '#667eea',
    },
    {
      id: 'portfolio',
      name: 'Creative Portfolio',
      description: 'Showcase your work with stunning visual layouts',
      image: 'https://via.placeholder.com/300x200/10B981/FFFFFF?text=Portfolio',
      features: ['Gallery', 'Project Showcase', 'About Section', 'Contact'],
      color: '#10B981',
    },
    {
      id: 'ecommerce',
      name: 'E-commerce Store',
      description: 'Complete online store with shopping cart and payments',
      image: 'https://via.placeholder.com/300x200/F59E0B/FFFFFF?text=E-commerce',
      features: ['Product Catalog', 'Shopping Cart', 'Payment Gateway', 'Orders'],
      color: '#F59E0B',
    },
    {
      id: 'blog',
      name: 'Blog & News',
      description: 'Share your thoughts and news with the world',
      image: 'https://via.placeholder.com/300x200/EF4444/FFFFFF?text=Blog',
      features: ['Blog Posts', 'Categories', 'Comments', 'Newsletter'],
      color: '#EF4444',
    },
    {
      id: 'restaurant',
      name: 'Restaurant & Food',
      description: 'Delicious websites for restaurants and food businesses',
      image: 'https://via.placeholder.com/300x200/8B5CF6/FFFFFF?text=Restaurant',
      features: ['Menu Display', 'Reservations', 'Gallery', 'Reviews'],
      color: '#8B5CF6',
    },
    {
      id: 'agency',
      name: 'Digital Agency',
      description: 'Modern design for creative agencies and startups',
      image: 'https://via.placeholder.com/300x200/06B6D4/FFFFFF?text=Agency',
      features: ['Case Studies', 'Team Profiles', 'Services', 'Blog'],
      color: '#06B6D4',
    },
  ];

  // Content types
  const contentTypes = [
    { id: 'news', name: 'News', icon: 'newspaper', color: '#667eea' },
    { id: 'advertisement', name: 'Advertisements', icon: 'megaphone', color: '#10B981' },
    { id: 'page', name: 'Pages', icon: 'document-text', color: '#F59E0B' },
    { id: 'service', name: 'Services', icon: 'construct', color: '#EF4444' },
    { id: 'product', name: 'Products', icon: 'cube', color: '#8B5CF6' },
  ];

  useEffect(() => {
    initializeScreen();
  }, []);

  useEffect(() => {
    // Animate screen entrance
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  // Enhanced Preview Functions
  const previewWebsite = (website) => {
    if (!website) {
      Alert.alert('Error', 'No website selected for preview');
      return;
    }

    setPreviewType('website');
    setPreviewContent(website);
    setShowPreviewModal(true);
  };

  const previewContentItem = (content) => {
    if (!content) {
      Alert.alert('Error', 'No content selected for preview');
      return;
    }

    setPreviewType('content');
    setPreviewContent(content);
    setShowPreviewModal(true);
  };

  // Enhanced Preview Modal without WebView
  const renderPreviewModal = () => (
    <Modal
      visible={showPreviewModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowPreviewModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowPreviewModal(false)}
          >
            <Ionicons name="close" size={24} color="#8E8E93" />
          </TouchableOpacity>
          
          <Text style={styles.modalTitle}>
            <Text>{previewType === 'website' ? 'Website Preview' : 'Content Preview'}</Text>
          </Text>
          
          <TouchableOpacity
            style={styles.modalSaveButton}
            onPress={() => {
              if (previewType === 'website' && previewContent?.isPublished) {
                openWebsite(previewContent);
              } else {
                Alert.alert(
                  'Publish Required',
                  previewType === 'website' 
                    ? 'Please publish the website to view it live.'
                    : 'Please publish the content to view it on your website.'
                );
              }
            }}
          >
            <Ionicons name="open" size={16} color="#FFFFFF" />
            <Text style={styles.modalSaveButtonText}>View Live</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalBody}>
          <View style={styles.previewContent}>
            <View style={styles.previewHeaderCard}>
              <Ionicons 
                name={previewType === 'website' ? 'globe' : 'document-text'} 
                size={48} 
                color="#667eea" 
              />
              <Text style={styles.previewTitle}>
                {previewType === 'website' 
                  ? previewContent?.name || 'Website'
                  : previewContent?.title || 'Content'
                }
              </Text>
              <Text style={styles.previewSubtitle}>
                {previewType === 'website' 
                  ? `Domain: ${previewContent?.domain || 'Not set'}`
                  : `Type: ${contentTypes.find(t => t.id === previewContent?.type)?.name || 'Content'}`
                }
              </Text>
            </View>

            {previewType === 'website' ? (
              <View style={styles.previewDetails}>
                <Text style={styles.previewSectionTitle}>Website Details</Text>
                <View style={styles.previewDetailItem}>
                  <Text style={styles.previewDetailLabel}>Template:</Text>
                  <Text style={styles.previewDetailValue}>
                    {websiteTemplates.find(t => t.id === previewContent?.template)?.name || 'Business'}
                  </Text>
                </View>
                <View style={styles.previewDetailItem}>
                  <Text style={styles.previewDetailLabel}>Title:</Text>
                  <Text style={styles.previewDetailValue}>{previewContent?.title || 'Not set'}</Text>
                </View>
                <View style={styles.previewDetailItem}>
                  <Text style={styles.previewDetailLabel}>Description:</Text>
                  <Text style={styles.previewDetailValue}>{previewContent?.description || 'Not set'}</Text>
                </View>
                <View style={styles.previewDetailItem}>
                  <Text style={styles.previewDetailLabel}>Status:</Text>
                  <View style={[
                    styles.statusBadge,
                    { backgroundColor: previewContent?.isPublished ? '#10B98120' : '#F59E0B20' }
                  ]}>
                    <Text style={[
                      styles.statusBadgeText,
                      { color: previewContent?.isPublished ? '#10B981' : '#F59E0B' }
                    ]}>
                      <Text>{previewContent?.isPublished ? 'Published' : 'Draft'}</Text>
                    </Text>
                  </View>
                </View>
              </View>
            ) : (
              <View style={styles.previewDetails}>
                <Text style={styles.previewSectionTitle}>Content Details</Text>
                <View style={styles.previewDetailItem}>
                  <Text style={styles.previewDetailLabel}>Title:</Text>
                  <Text style={styles.previewDetailValue}>{previewContent?.title || 'Not set'}</Text>
                </View>
                <View style={styles.previewDetailItem}>
                  <Text style={styles.previewDetailLabel}>Excerpt:</Text>
                  <Text style={styles.previewDetailValue}>{previewContent?.excerpt || 'Not set'}</Text>
                </View>
                <View style={styles.previewDetailItem}>
                  <Text style={styles.previewDetailLabel}>Author:</Text>
                  <Text style={styles.previewDetailValue}>{previewContent?.author || 'Not set'}</Text>
                </View>
                <View style={styles.previewDetailItem}>
                  <Text style={styles.previewDetailLabel}>Status:</Text>
                  <View style={[
                    styles.statusBadge,
                    { backgroundColor: previewContent?.isPublished ? '#10B98120' : '#F59E0B20' }
                  ]}>
                    <Text style={[
                      styles.statusBadgeText,
                      { color: previewContent?.isPublished ? '#10B981' : '#F59E0B' }
                    ]}>
                      <Text>{previewContent?.isPublished ? 'Published' : 'Draft'}</Text>
                    </Text>
                  </View>
                </View>
                {previewContent?.content && (
                  <View style={styles.contentPreview}>
                    <Text style={styles.previewSectionTitle}>Content Preview</Text>
                    <Text style={styles.contentPreviewText} numberOfLines={10}>
                      {previewContent.content}
                    </Text>
                  </View>
                )}
              </View>
            )}

            <View style={styles.previewActions}>
              <TouchableOpacity
                style={styles.previewActionButton}
                onPress={() => {
                  if (previewType === 'website') {
                    editWebsite(previewContent);
                  } else {
                    editContent(previewContent);
                  }
                  setShowPreviewModal(false);
                }}
              >
                <Ionicons name="create" size={20} color="#667eea" />
                <Text style={[styles.previewActionText, { color: '#667eea' }]}>Edit</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.previewActionButton}
                onPress={() => {
                  if (previewType === 'website') {
                    shareWebsite(previewContent);
                  } else {
                    // Share content
                    Share.share({
                      message: `Check out this content: ${previewContent.title}`,
                      title: previewContent.title,
                    });
                  }
                }}
              >
                <Ionicons name="share" size={20} color="#10B981" />
                <Text style={[styles.previewActionText, { color: '#10B981' }]}>Share</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // Enhanced content actions to include preview
  const enhancedContentActions = (content) => {
    Alert.alert(
      'Content Actions',
      'Choose an action',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Preview', onPress: () => previewContentItem(content) },
        { text: 'Edit', onPress: () => editContent(content) },
        { text: 'Duplicate', onPress: () => {
          const duplicated = { ...content, id: '', title: `${content.title} (Copy)` };
          setContentForm(duplicated);
          setShowContentModal(true);
        }},
        { text: 'Delete', style: 'destructive', onPress: () => deleteContent(content.id) },
      ]
    );
  };

  // Enhanced website actions to include preview
  const enhancedWebsiteActions = (website) => {
    Alert.alert(
      'Website Actions',
      'Choose an action',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Preview', onPress: () => previewWebsite(website) },
        { text: 'Edit', onPress: () => editWebsite(website) },
        { text: 'Share', onPress: () => shareWebsite(website) },
        website.isPublished && { text: 'Open Live', onPress: () => openWebsite(website) },
        !website.isPublished && { text: 'Publish', onPress: () => publishWebsite(website.id) },
        { text: 'Delete', style: 'destructive', onPress: () => deleteWebsite(website.id) },
      ].filter(Boolean)
    );
  };

  // AI System Prompts
  const getSystemPrompt = (type) => {
    const prompts = {
      news: `You are a professional journalist and content writer. Create engaging, well-structured news content with:
      - Catchy, attention-grabbing title
      - Well-structured content with clear paragraphs and sections
      - Compelling excerpt/summary that hooks the reader
      - SEO-optimized meta title and description
      - Relevant keywords for search engines
      Format your response as valid JSON: {"title": "", "content": "", "excerpt": "", "seo": {"metaTitle": "", "metaDescription": "", "keywords": ""}}`,
      
      advertisement: `You are a marketing expert and copywriter. Create persuasive, conversion-focused advertisement content with:
      - Attention-grabbing title that creates urgency
      - Persuasive content highlighting benefits and features
      - Strong call-to-action
      - Concise, compelling excerpt
      - SEO-optimized meta information
      Format your response as valid JSON: {"title": "", "content": "", "excerpt": "", "seo": {"metaTitle": "", "metaDescription": "", "keywords": ""}}`,
      
      page: `You are a web content specialist. Create informative, user-friendly page content with:
      - Clear, descriptive title that sets expectations
      - Well-organized content with proper sections and headings
      - Helpful excerpt that summarizes the page
      - SEO-optimized structure and meta information
      Format your response as valid JSON: {"title": "", "content": "", "excerpt": "", "seo": {"metaTitle": "", "metaDescription": "", "keywords": ""}}`,
      
      service: `You are a business service writer. Create professional service description content with:
      - Professional service title that highlights value
      - Detailed service description with features, benefits, and differentiators
      - Concise service summary for quick scanning
      - SEO-optimized for service pages and local search
      Format your response as valid JSON: {"title": "", "content": "", "excerpt": "", "seo": {"metaTitle": "", "metaDescription": "", "keywords": ""}}`,
      
      product: `You are an e-commerce product writer. Create compelling product description content with:
      - Engaging product title with key features
      - Detailed product description highlighting benefits and specifications
      - Compelling product summary for quick decisions
      - SEO-optimized for product pages and e-commerce search
      Format your response as valid JSON: {"title": "", "content": "", "excerpt": "", "seo": {"metaTitle": "", "metaDescription": "", "keywords": ""}}`
    };
    
    return prompts[type] || prompts.news;
  };

  // Parse AI Response
  const parseAIResponse = (content, type) => {
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(content);
      return parsed;
    } catch (error) {
      // If JSON parsing fails, structure the response manually
      console.log('JSON parsing failed, using manual parsing');
      const lines = content.split('\n').filter(line => line.trim());
      return {
        title: lines[0]?.replace('Title:', '').replace('"', '').trim() || `AI Generated ${type}`,
        content: content,
        excerpt: lines.slice(0, 3).join(' ').substring(0, 150) + '...',
        seo: {
          metaTitle: (lines[0]?.replace('Title:', '').replace('"', '').trim() || `AI Generated ${type}`).substring(0, 60),
          metaDescription: lines.slice(0, 2).join(' ').substring(0, 160),
          keywords: `${type}, ai generated, content, ${lines[0]?.split(' ').slice(0, 3).join(', ')}`
        }
      };
    }
  };

  // Local Fallback Content Generator
  const generateLocalFallbackContent = (prompt, type) => {
    const fallbacks = {
      news: {
        title: `Breaking News: ${prompt}`,
        content: `This is a comprehensive news article about "${prompt}". While our AI services are currently unavailable, this template provides a solid foundation for your news content. You can customize it with specific details, quotes, and updates.\n\nKey points to cover:\n• Background and context\n• Current developments\n• Impact and implications\n• Future outlook\n• Expert opinions if available`,
        excerpt: `Comprehensive coverage of ${prompt} with latest developments and analysis.`,
        seo: {
          metaTitle: `${prompt} - Latest News & Updates`,
          metaDescription: `Stay informed about ${prompt}. Get the latest news, developments, and comprehensive coverage.`,
          keywords: `${prompt}, news, updates, latest, coverage`
        }
      },
      advertisement: {
        title: `Special Offer: ${prompt}`,
        content: `🚀 LIMITED TIME OFFER! 🚀\n\nDon't miss this incredible opportunity! ${prompt} - available now with exclusive benefits and premium features.\n\n✨ WHAT YOU GET:\n• Premium quality service\n• Exclusive benefits\n• Limited time pricing\n• Special bonuses\n• Full support\n\n⏰ ACT NOW - This offer won't last!`,
        excerpt: `Limited time special offer for ${prompt} with exclusive benefits and premium features.`,
        seo: {
          metaTitle: `Special Offer - ${prompt} | Limited Time`,
          metaDescription: `Limited time special offer for ${prompt}. Get exclusive benefits and premium features at an incredible price.`,
          keywords: `${prompt}, offer, special, limited time, discount`
        }
      },
      page: {
        title: `About ${prompt}`,
        content: `# Welcome to Our ${prompt} Page\n\nThis page provides comprehensive information about ${prompt}. We are committed to delivering valuable content and excellent user experience.\n\n## Overview\nLearn everything you need to know about ${prompt} in this detailed guide.\n\n## Features & Benefits\n• Comprehensive coverage\n• Easy to understand\n• Regularly updated\n• User-focused content\n\n## Get Started\nExplore our resources and discover how ${prompt} can benefit you.`,
        excerpt: `Comprehensive information page about ${prompt} with detailed coverage and resources.`,
        seo: {
          metaTitle: `${prompt} - Complete Guide & Information`,
          metaDescription: `Learn everything about ${prompt}. Comprehensive guide with detailed information and valuable insights.`,
          keywords: `${prompt}, guide, information, comprehensive, details`
        }
      },
      service: {
        title: `Professional ${prompt} Services`,
        content: `# ${prompt} Services\n\nOur professional ${prompt} services are designed to deliver exceptional results and outstanding value.\n\n## Service Features\n✅ Expert implementation\n✅ Quality guaranteed\n✅ Professional support\n✅ Competitive pricing\n✅ Timely delivery\n\n## Why Choose Us?\n• Years of experience in ${prompt}\n• Certified professionals\n• Customer-focused approach\n• Proven track record\n• Comprehensive support`,
        excerpt: `Professional ${prompt} services with expert implementation, quality guarantee, and comprehensive support.`,
        seo: {
          metaTitle: `${prompt} Services - Professional & Reliable`,
          metaDescription: `Professional ${prompt} services with expert implementation, quality guarantee, and comprehensive support. Get started today!`,
          keywords: `${prompt}, services, professional, expert, quality`
        }
      },
      product: {
        title: `${prompt} - Premium Quality`,
        content: `# ${prompt} - Premium Product\n\nDiscover our exceptional ${prompt} product, designed with quality, performance, and value in mind.\n\n## Product Features\n⭐ High-quality materials\n⭐ Excellent performance\n⭐ User-friendly design\n⭐ Great value\n⭐ Reliable operation\n\n## Specifications\n• Premium construction\n• Advanced features\n• Easy to use\n• Durable design\n• Affordable pricing`,
        excerpt: `Premium ${prompt} product with high-quality materials, excellent performance, and great value.`,
        seo: {
          metaTitle: `${prompt} - Premium Quality Product`,
          metaDescription: `High-quality ${prompt} product with excellent performance, premium features, and great value. Discover the difference!`,
          keywords: `${prompt}, product, premium, quality, performance`
        }
      }
    };
    
    return fallbacks[type] || fallbacks.news;
  };

  // OpenAI API Integration
  const generateWithOpenAI = async (prompt, type) => {
    if (!apiKeys.openai || apiKeys.openai === 'your-openai-api-key-here') {
      throw new Error('OpenAI API key not configured');
    }

    const systemPrompt = getSystemPrompt(type);
    
    const response = await axios.post(
      'https://api.openai.com/v1/chat/completions',
      {
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 1500,
        temperature: 0.7
      },
      {
        headers: {
          'Authorization': `Bearer ${apiKeys.openai}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000
      }
    );

    const content = response.data.choices[0].message.content;
    return parseAIResponse(content, type);
  };

  // OpenRouter API Integration
  const generateWithOpenRouter = async (prompt, type) => {
    if (!apiKeys.openrouter || apiKeys.openrouter === 'your-openrouter-api-key-here') {
      throw new Error('OpenRouter API key not configured');
    }

    const systemPrompt = getSystemPrompt(type);
    
    const response = await axios.post(
      'https://openrouter.ai/api/v1/chat/completions',
      {
        model: 'meta-llama/llama-3-70b-instruct',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 1500
      },
      {
        headers: {
          'Authorization': `Bearer ${apiKeys.openrouter}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': 'https://yourapp.com',
          'X-Title': 'Website Management App'
        },
        timeout: 30000
      }
    );

    const content = response.data.choices[0].message.content;
    return parseAIResponse(content, type);
  };

  // Hugging Face API Integration
  const generateWithHuggingFace = async (prompt, type) => {
    if (!apiKeys.huggingface || apiKeys.huggingface === 'your-huggingface-api-key-here') {
      throw new Error('Hugging Face API key not configured');
    }

    const systemPrompt = getSystemPrompt(type);
    const fullPrompt = `${systemPrompt}\n\nUser: ${prompt}\n\nAssistant:`;
    
    const response = await axios.post(
      'https://api-inference.huggingface.co/models/microsoft/DialoGPT-large',
      {
        inputs: fullPrompt,
        parameters: {
          max_length: 1500,
          temperature: 0.7,
          do_sample: true
        }
      },
      {
        headers: {
          'Authorization': `Bearer ${apiKeys.huggingface}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000
      }
    );

    const content = response.data[0].generated_text;
    return parseAIResponse(content, type);
  };

  // AI Call with Fallbacks
  const callAIWithFallback = async (prompt, type) => {
    try {
      // Try OpenAI first
      console.log('Trying OpenAI...');
      return await generateWithOpenAI(prompt, type);
    } catch (error) {
      console.log('OpenAI failed:', error.message);
      try {
        // Try OpenRouter second
        console.log('Trying OpenRouter...');
        return await generateWithOpenRouter(prompt, type);
      } catch (error2) {
        console.log('OpenRouter failed:', error2.message);
        try {
          // Try Hugging Face third
          console.log('Trying Hugging Face...');
          return await generateWithHuggingFace(prompt, type);
        } catch (error3) {
          console.log('All AI providers failed, using local fallback');
          // Use local simulation as final fallback
          return generateLocalFallbackContent(prompt, type);
        }
      }
    }
  };

  // Main AI Content Generation
  const generateAiContent = async (prompt, type = 'news') => {
    if (!prompt.trim()) {
      Alert.alert('Error', 'Please enter a prompt');
      return;
    }

    setIsAiGenerating(true);
    
    try {
      const generatedContent = await callAIWithFallback(prompt, type);

      setContentForm(prev => ({
        ...prev,
        ...generatedContent,
        type: type
      }));
      
      setShowAiModal(false);
      setAiPrompt('');
      setShowContentModal(true);
      
      Alert.alert('🎉 AI Content Generated', 'AI has successfully generated your content. You can now review and edit it before saving.');
      
    } catch (error) {
      console.error('AI generation error:', error);
      Alert.alert('AI Error', 'Failed to generate content. Please check your API keys and try again.');
    } finally {
      setIsAiGenerating(false);
    }
  };

  // AI Suggestions
  const getAiSuggestions = async () => {
    setIsAiGenerating(true);
    try {
      // Simulate AI suggestion API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const suggestions = [
        {
          id: '1',
          title: 'Website SEO Optimization',
          description: 'Improve your website search ranking',
          type: 'seo',
          action: () => generateSeoOptimization()
        },
        {
          id: '2',
          title: 'Content Strategy Plan',
          description: 'Get personalized content recommendations',
          type: 'strategy',
          action: () => generateContentStrategy()
        },
        {
          id: '3',
          title: 'Social Media Posts',
          description: 'Generate engaging social media content',
          type: 'social',
          action: () => generateSocialMediaContent()
        },
        {
          id: '4',
          title: 'Email Newsletter',
          description: 'Create compelling newsletter content',
          type: 'newsletter',
          action: () => generateNewsletterContent()
        }
      ];
      
      setAiSuggestions(suggestions);
    } catch (error) {
      console.error('Error getting AI suggestions:', error);
    } finally {
      setIsAiGenerating(false);
    }
  };

  // SEO Optimization with AI
  const generateSeoOptimization = async () => {
    if (!selectedWebsite) {
      Alert.alert('Error', 'Please select a website first');
      return;
    }

    setIsAiGenerating(true);
    try {
      const websiteName = selectedWebsite.name || 'Website';
      const websiteDescription = selectedWebsite.description || '';
      const templateType = selectedWebsite.template || 'business';
      const domain = selectedWebsite.domain || '';

      const prompt = `Analyze and provide SEO optimization for a website about "${websiteName}". 
      Website description: "${websiteDescription}"
      Template type: "${templateType}"
      Domain: "${domain}"
      
      Provide comprehensive SEO recommendations in JSON format:
      {
        "metaTitle": "optimized meta title",
        "metaDescription": "optimized meta description", 
        "keywords": "relevant, keywords, here",
        "recommendations": ["recommendation 1", "recommendation 2"]
      }`;

      const response = await callAIWithFallback(prompt, 'page');
      
      // Safely handle the response
      let seoData;
      if (response && (response.seo || response.recommendations)) {
        seoData = {
          metaTitle: response.seo?.metaTitle || response.metaTitle || `${websiteName} - Professional Services`,
          metaDescription: response.seo?.metaDescription || response.metaDescription || `Discover ${websiteName} - ${websiteDescription}`,
          keywords: response.seo?.keywords || response.keywords || `${websiteName}, services, professional`,
          recommendations: response.recommendations || [
            'Optimize page loading speed',
            'Add descriptive alt text to images',
            'Create XML sitemap',
            'Build quality backlinks'
          ]
        };
      } else {
        // Fallback structure
        seoData = {
          metaTitle: response?.title || `${websiteName} - Professional Services`,
          metaDescription: response?.excerpt || `Discover ${websiteName} - ${websiteDescription}`,
          keywords: response?.seo?.keywords || `${websiteName}, services, professional`,
          recommendations: [
            'Optimize page loading speed',
            'Add descriptive alt text to images',
            'Create XML sitemap',
            'Build quality backlinks'
          ]
        };
      }
      
      setWebsiteForm(prev => ({
        ...prev,
        seo: {
          ...prev.seo,
          ...seoData
        }
      }));
      
      setShowWebsiteModal(true);
      Alert.alert('✅ SEO Optimization Complete', 'AI has generated optimized SEO settings for your website.');
    } catch (error) {
      console.error('SEO optimization error:', error);
      Alert.alert('Error', 'Failed to generate SEO optimization');
    } finally {
      setIsAiGenerating(false);
    }
  };

  // Content Strategy with AI
  const generateContentStrategy = async () => {
    setIsAiGenerating(true);
    try {
      const prompt = `Create a comprehensive 3-month content strategy for a ${selectedWebsite?.template || 'business'} website about "${selectedWebsite?.name || 'our business'}". 
      Include content recommendations, posting schedule, and goals.`;
      
      const response = await callAIWithFallback(prompt, 'page');
      
      Alert.alert(
        '📊 Content Strategy Generated',
        `AI has created a comprehensive content strategy:\n\n${response.content || 'Strategy generated successfully. Check the content for details.'}`,
        [{ text: 'OK' }]
      );
    } catch (error) {
      console.error('Content strategy error:', error);
    } finally {
      setIsAiGenerating(false);
    }
  };

  // Social Media Content with AI
  const generateSocialMediaContent = async () => {
    setIsAiGenerating(true);
    try {
      const prompt = `Create engaging social media content for ${selectedWebsite?.name || 'our business'} including posts for Facebook, Instagram, Twitter, and LinkedIn.`;
      
      const response = await callAIWithFallback(prompt, 'advertisement');
      
      Alert.alert(
        '📱 Social Media Content',
        `AI-generated social media content:\n\n${response.content || 'Social media content generated successfully. Check the content for details.'}`,
        [{ text: 'OK' }]
      );
    } catch (error) {
      console.error('Social media content error:', error);
    } finally {
      setIsAiGenerating(false);
    }
  };

  // Newsletter Content with AI
  const generateNewsletterContent = async () => {
    setIsAiGenerating(true);
    try {
      const prompt = `Create a professional email newsletter for ${selectedWebsite?.name || 'our company'} including subject line, content, and call-to-action.`;
      
      const response = await callAIWithFallback(prompt, 'news');
      
      setContentForm(prev => ({
        ...prev,
        type: 'news',
        title: response.title || `Monthly Update from ${selectedWebsite?.name || 'Our Company'}`,
        content: response.content || 'Newsletter content',
        excerpt: response.excerpt || 'Monthly newsletter with updates and insights'
      }));
      
      setShowContentModal(true);
      Alert.alert('📧 Newsletter Generated', 'AI has created a newsletter template. You can now customize it further.');
    } catch (error) {
      console.error('Newsletter generation error:', error);
    } finally {
      setIsAiGenerating(false);
    }
  };

  // Save API Keys
  const saveApiKeys = async () => {
    try {
      await AsyncStorage.setItem('ai_api_keys', JSON.stringify(apiKeys));
      Alert.alert('Success', 'API keys saved successfully!');
      setShowApiKeyModal(false);
    } catch (error) {
      console.error('Error saving API keys:', error);
      Alert.alert('Error', 'Failed to save API keys');
    }
  };

  // Load API Keys
  const loadApiKeys = async () => {
    try {
      const storedKeys = await AsyncStorage.getItem('ai_api_keys');
      if (storedKeys) {
        setApiKeys(JSON.parse(storedKeys));
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
    }
  };

  const loadCompanyProfile = async () => {
    try {
      const businessId = await getBusinessId();
      if (businessId) setCompanyId(businessId);

      // Try cached profile first
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const profile = JSON.parse(cached);
        setCompanyProfile(profile);
        setCompanyLogo(profile?.logo || profile?.logo_url || profile?.company_logo || profile?.banner_image_url || null);
      }

      // Fetch fresh from API
      if (businessId) {
        try {
          const res = await api.get(`/api/company-profile/${parseInt(businessId) || 0}`);
          if (res.data?.success && res.data.data) {
            const raw = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
            if (raw) {
              setCompanyProfile(raw);
              setCompanyLogo(raw.logo || raw.logo_url || raw.company_logo || raw.banner_image_url || null);
              await AsyncStorage.setItem('company_profile', JSON.stringify(raw));
            }
          }
        } catch (err) {
          console.warn('Company profile API error, using cached:', err?.message);
        }
      }
    } catch (error) {
      console.warn('Error loading company profile:', error);
    }
  };

    const initializeScreen = async () => {
    try {
      setIsLoading(true);
      // Load company profile first
      await loadCompanyProfile();
      await loadApiKeys();
      await loadWebsites();
      await loadAnalytics();
      await getAiSuggestions();
      simulateSync();
    } catch (error) {
      console.error('Error initializing screen:', error);
      Alert.alert('Error', 'Failed to load website data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadWebsites = async () => {
    try {
      // ✅ Try API first
      if (companyId) {
        try {
          const res = await api.get('/api/data/company_websites', {
            params: { company_id: parseInt(companyId), limit: 100 },
          });
          if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
            const apiWebsites = res.data.data;
            setWebsites(apiWebsites);
            if (apiWebsites.length > 0 && !selectedWebsite) {
              setSelectedWebsite(apiWebsites[0]);
              await loadWebsiteContent(apiWebsites[0].id);
            }
            // Cache locally
            await AsyncStorage.setItem(`websites_${companyId}`, JSON.stringify(apiWebsites));
            console.log(`✅ Loaded ${apiWebsites.length} websites from API`);
            return;
          }
        } catch (apiErr) {
          console.warn('API website fetch failed, trying cache:', apiErr?.message);
        }
      }

      // Fallback: AsyncStorage cache
      const stored = await AsyncStorage.getItem(`websites_${companyId || 'default'}`);
      if (stored) {
        const parsedWebsites = JSON.parse(stored);
        setWebsites(parsedWebsites);
        if (parsedWebsites.length > 0 && !selectedWebsite) {
          setSelectedWebsite(parsedWebsites[0]);
          await loadWebsiteContent(parsedWebsites[0].id);
        }
      } else {
        // Create sample website pre-filled from company profile
        const sampleWebsite = {
          id: `ws_${Date.now()}`,
          company_id: companyId ? parseInt(companyId) : null,
          name: companyProfile?.company_name || companyProfile?.name || 'My Business Website',
          domain: companyProfile?.website?.replace(/^https?:\/\//, '') || 'mybusiness.com',
          template: 'business',
          title: `Welcome to ${companyProfile?.company_name || companyProfile?.name || 'My Business'}`,
          description: companyProfile?.description || 'Professional services and solutions for all your needs',
          logo: companyLogo || '',
          contact: {
            email: companyProfile?.email || '',
            phone: companyProfile?.phone || '',
            address: companyProfile?.headquarters || companyProfile?.address || '',
            website: companyProfile?.website || '',
          },
          isPublished: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          stats: { visitors: 0, pageViews: 0, bounceRate: 0, avgSessionDuration: 0 },
        };
        setWebsites([sampleWebsite]);
        setSelectedWebsite(sampleWebsite);
        await AsyncStorage.setItem(`websites_${companyId || 'default'}`, JSON.stringify([sampleWebsite]));
        // Also push to API
        if (companyId) {
          api.post('/api/data/company_websites', sampleWebsite).catch(() => {});
        }
      }
    } catch (error) {
      console.error('Error loading websites:', error);
    }
  };

  const loadWebsiteContent = async (websiteId) => {
    try {
      // ✅ Try API first
      if (companyId && websiteId) {
        try {
          const res = await api.get('/api/data/website_content', {
            params: { website_id: websiteId, company_id: parseInt(companyId), limit: 500 },
          });
          if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
            // Group by content type
            const grouped = { news: [], advertisements: [], pages: [], services: [], products: [] };
            res.data.data.forEach((item) => {
              const type = item.type || 'news';
              const bucket = type === 'advertisement' ? 'advertisements' : type === 'page' ? 'pages' : type === 'service' ? 'services' : type === 'product' ? 'products' : 'news';
              if (grouped[bucket]) grouped[bucket].push(item);
            });
            setWebsiteContent(grouped);
            await AsyncStorage.setItem(`website_content_${websiteId}`, JSON.stringify(grouped));
            console.log(`✅ Loaded ${res.data.data.length} content items from API`);
            return;
          }
        } catch (apiErr) {
          console.warn('API content fetch failed, trying cache:', apiErr?.message);
        }
      }

      // Fallback: AsyncStorage cache
      const stored = await AsyncStorage.getItem(`website_content_${websiteId}`);
      if (stored) {
        setWebsiteContent(JSON.parse(stored));
      } else {
        const companyName = companyProfile?.company_name || companyProfile?.name || 'Our Company';
        const sampleContent = {
          news: [{
            id: `n_${Date.now()}`,
            type: 'news',
            website_id: websiteId,
            company_id: companyId ? parseInt(companyId) : null,
            title: `Welcome to ${companyName}`,
            content: `We are excited to announce the launch of the new ${companyName} website with improved features and better user experience.`,
            excerpt: `Announcing the launch of the ${companyName} website.`,
            author: companyName,
            isPublished: true,
            publishDate: new Date().toISOString(),
            createdAt: new Date().toISOString(),
          }],
          advertisements: [],
          pages: [{
            id: `p_${Date.now()}`,
            type: 'page',
            website_id: websiteId,
            company_id: companyId ? parseInt(companyId) : null,
            title: 'About Us',
            content: companyProfile?.description || `Learn more about ${companyName}, our mission, values, and the team behind our success.`,
            excerpt: `Discover the story behind ${companyName}.`,
            author: companyName,
            isPublished: true,
            publishDate: new Date().toISOString(),
            createdAt: new Date().toISOString(),
          }],
          services: [],
          products: [],
        };
        setWebsiteContent(sampleContent);
        await AsyncStorage.setItem(`website_content_${websiteId}`, JSON.stringify(sampleContent));
      }
    } catch (error) {
      console.error('Error loading website content:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      // Simulate analytics data
      const analyticsData = {
        totalVisitors: 1250 + Math.floor(Math.random() * 100),
        pageViews: 3420 + Math.floor(Math.random() * 200),
        bounceRate: 35 + Math.floor(Math.random() * 10),
        avgSessionDuration: 180 + Math.floor(Math.random() * 60),
        topPages: [
          { path: '/', views: 1200, title: 'Home' },
          { path: '/services', views: 890, title: 'Services' },
          { path: '/about', views: 650, title: 'About Us' },
          { path: '/contact', views: 420, title: 'Contact' },
          { path: '/blog', views: 380, title: 'Blog' },
        ],
        recentActivity: [
          { action: 'Page View', page: 'Home', time: '2 minutes ago' },
          { action: 'Contact Form', page: 'Contact', time: '5 minutes ago' },
          { action: 'Page View', page: 'Services', time: '8 minutes ago' },
          { action: 'Sign Up', page: 'Newsletter', time: '12 minutes ago' },
          { action: 'Download', page: 'Resources', time: '15 minutes ago' },
        ],
      };
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const simulateSync = () => {
    setSyncStatus('syncing');
    setTimeout(() => {
      setSyncStatus('connected');
    }, 2000);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadWebsites();
    await loadAnalytics();
    await getAiSuggestions();
    setRefreshing(false);
  };

  const saveWebsite = async () => {
    try {
      if (!websiteForm.name || !websiteForm.domain || !websiteForm.title) {
        Alert.alert('Error', 'Please fill in all required fields');
        return;
      }

      setSyncStatus('syncing');

      const websiteData = {
        ...websiteForm,
        id: websiteForm.id || `ws_${Date.now()}`,
        company_id: companyId ? parseInt(companyId) : null,
        logo: websiteForm.logo || companyLogo || '',
        updatedAt: new Date().toISOString(),
      };

      let updatedWebsites;
      if (websiteForm.id) {
        updatedWebsites = websites.map(w => w.id === websiteForm.id ? websiteData : w);
        // ✅ PUT to API
        if (companyId) {
          api.put(`/api/data/company_websites/${websiteForm.id}`, websiteData).catch((e) =>
            console.warn('API website update failed (cached locally):', e?.message)
          );
        }
      } else {
        websiteData.createdAt = new Date().toISOString();
        websiteData.stats = { visitors: 0, pageViews: 0, bounceRate: 0, avgSessionDuration: 0 };
        updatedWebsites = [...websites, websiteData];
        // ✅ POST to API
        if (companyId) {
          api.post('/api/data/company_websites', websiteData).catch((e) =>
            console.warn('API website create failed (cached locally):', e?.message)
          );
        }
      }

      setWebsites(updatedWebsites);
      setSelectedWebsite(websiteData);
      await AsyncStorage.setItem(`websites_${companyId || 'default'}`, JSON.stringify(updatedWebsites));
      
      setSyncStatus('connected');
      setShowWebsiteModal(false);
      resetWebsiteForm();
      Alert.alert('Success', 'Website saved successfully!');
    } catch (error) {
      console.error('Error saving website:', error);
      setSyncStatus('error');
      Alert.alert('Error', 'Failed to save website');
    }
  };

  const saveContent = async () => {
    try {
      if (!selectedWebsite) {
        Alert.alert('Error', 'Please select a website first');
        return;
      }

      if (!contentForm.title || !contentForm.content) {
        Alert.alert('Error', 'Please fill in title and content');
        return;
      }

      setSyncStatus('syncing');

      const contentData = {
        ...contentForm,
        id: contentForm.id || `c_${Date.now()}`,
        website_id: selectedWebsite.id,
        company_id: companyId ? parseInt(companyId) : null,
        author: contentForm.author || companyProfile?.company_name || companyProfile?.name || '',
        updatedAt: new Date().toISOString(),
      };

      const updatedContent = { ...websiteContent };
      
      if (contentForm.id) {
        updatedContent[contentType] = updatedContent[contentType].map(c => 
          c.id === contentForm.id ? contentData : c
        );
        // ✅ PUT to API
        if (companyId) {
          api.put(`/api/data/website_content/${contentForm.id}`, contentData).catch((e) =>
            console.warn('API content update failed (cached locally):', e?.message)
          );
        }
      } else {
        contentData.createdAt = new Date().toISOString();
        updatedContent[contentType] = [...(updatedContent[contentType] || []), contentData];
        // ✅ POST to API
        if (companyId) {
          api.post('/api/data/website_content', contentData).catch((e) =>
            console.warn('API content create failed (cached locally):', e?.message)
          );
        }
      }

      setWebsiteContent(updatedContent);
      await AsyncStorage.setItem(`website_content_${selectedWebsite.id}`, JSON.stringify(updatedContent));
      
      setSyncStatus('connected');
      setShowContentModal(false);
      resetContentForm();
      Alert.alert('Success', 'Content saved successfully!');
    } catch (error) {
      console.error('Error saving content:', error);
      setSyncStatus('error');
      Alert.alert('Error', 'Failed to save content');
    }
  };

  const deleteWebsite = async (websiteId) => {
    try {
      Alert.alert(
        'Delete Website',
        'Are you sure you want to delete this website? This action cannot be undone.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              const updatedWebsites = websites.filter(w => w.id !== websiteId);
              setWebsites(updatedWebsites);
              await AsyncStorage.setItem(`websites_${companyId || 'default'}`, JSON.stringify(updatedWebsites));
              
              if (selectedWebsite?.id === websiteId) {
                setSelectedWebsite(updatedWebsites[0] || null);
              }
              
              await AsyncStorage.removeItem(`website_content_${websiteId}`);

              // ✅ DELETE from API
              if (companyId) {
                api.delete(`/api/data/company_websites/${websiteId}`).catch((e) =>
                  console.warn('API website delete failed:', e?.message)
                );
              }
              
              Alert.alert('Success', 'Website deleted successfully');
            }
          }
        ]
      );
    } catch (error) {
      console.error('Error deleting website:', error);
      Alert.alert('Error', 'Failed to delete website');
    }
  };

  const deleteContent = async (contentId) => {
    try {
      Alert.alert(
        'Delete Content',
        'Are you sure you want to delete this content?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              const updatedContent = { ...websiteContent };
              updatedContent[contentType] = updatedContent[contentType].filter(c => c.id !== contentId);
              
              setWebsiteContent(updatedContent);
              await AsyncStorage.setItem(`website_content_${selectedWebsite.id}`, JSON.stringify(updatedContent));
              
              // ✅ DELETE from API
              if (companyId) {
                api.delete(`/api/data/website_content/${contentId}`).catch((e) =>
                  console.warn('API content delete failed:', e?.message)
                );
              }
              
              Alert.alert('Success', 'Content deleted successfully');
            }
          }
        ]
      );
    } catch (error) {
      console.error('Error deleting content:', error);
      Alert.alert('Error', 'Failed to delete content');
    }
  };

  const publishWebsite = async (websiteId) => {
    try {
      setSyncStatus('syncing');
      
      const updatedWebsites = websites.map(w => 
        w.id === websiteId 
          ? { ...w, isPublished: true, publishedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
          : w
      );
      
      setWebsites(updatedWebsites);
      await AsyncStorage.setItem(`websites_${companyId || 'default'}`, JSON.stringify(updatedWebsites));

      // Update selected website if it's the one being published
      if (selectedWebsite?.id === websiteId) {
        setSelectedWebsite({ ...selectedWebsite, isPublished: true, publishedAt: new Date().toISOString() });
      }

      // ✅ PUT to API
      if (companyId) {
        api.put(`/api/data/company_websites/${websiteId}`, {
          isPublished: true,
          publishedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }).catch((e) => console.warn('API publish failed (cached locally):', e?.message));
      }
      
      setSyncStatus('connected');
      Alert.alert('🎉 Success', 'Website published successfully! Your website is now live.');
    } catch (error) {
      console.error('Error publishing website:', error);
      setSyncStatus('error');
      Alert.alert('Error', 'Failed to publish website');
    }
  };

  const shareWebsite = async (website) => {
    try {
      const url = `https://${website.domain}`;
      await Share.share({
        message: `Check out my website: ${website.title} - ${url}`,
        url: url,
        title: website.title,
      });
    } catch (error) {
      console.error('Error sharing website:', error);
    }
  };

  const openWebsite = (website) => {
    const url = `https://${website.domain}`;
    Linking.openURL(url).catch(err => {
      Alert.alert('Error', 'Could not open website. Please check the domain.');
    });
  };

  const resetWebsiteForm = () => {
    setWebsiteForm({
      id: '',
      name: companyProfile?.company_name || companyProfile?.name || '',
      domain: companyProfile?.website?.replace(/^https?:\/\//, '') || '',
      template: 'business',
      title: companyProfile?.company_name || companyProfile?.name || '',
      description: companyProfile?.description || '',
      logo: companyLogo || '',
      favicon: '',
      colors: {
        primary: '#667eea',
        secondary: '#764ba2',
        accent: '#10B981',
        background: '#FFFFFF',
        text: '#1C1C1E',
      },
      contact: {
        email: companyProfile?.email || '',
        phone: companyProfile?.phone || '',
        address: companyProfile?.headquarters || companyProfile?.address || '',
        website: companyProfile?.website || '',
      },
      social: {
        facebook: companyProfile?.social_facebook || '',
        twitter: companyProfile?.social_twitter || '',
        linkedin: companyProfile?.social_linkedin || '',
        instagram: companyProfile?.social_instagram || '',
        youtube: companyProfile?.social_youtube || '',
      },
      features: {
        blog: true,
        ecommerce: false,
        booking: false,
        gallery: true,
        testimonials: true,
        newsletter: false,
      },
      seo: {
        metaTitle: companyProfile?.company_name || companyProfile?.name || '',
        metaDescription: companyProfile?.description || '',
        keywords: companyProfile?.industry || '',
        googleAnalytics: '',
        facebookPixel: '',
      },
      isPublished: false,
      publishedAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  };

  const resetContentForm = () => {
    setContentForm({
      id: '',
      type: contentType,
      title: '',
      content: '',
      excerpt: '',
      featuredImage: '',
      gallery: [],
      category: '',
      tags: [],
      author: companyProfile?.company_name || companyProfile?.name || '',
      publishDate: new Date().toISOString(),
      isPublished: false,
      isFeatured: false,
      seo: {
        metaTitle: '',
        metaDescription: '',
        keywords: '',
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setEditingContent(null);
  };

  const editWebsite = (website) => {
    setWebsiteForm(website);
    setShowWebsiteModal(true);
  };

  const editContent = (content) => {
    setContentForm(content);
    setEditingContent(content);
    setShowContentModal(true);
  };

  const getSyncStatusColor = () => {
    switch (syncStatus) {
      case 'connected': return '#10B981';
      case 'syncing': return '#F59E0B';
      case 'error': return '#FF3B30';
      default: return '#8E8E93';
    }
  };

  const getSyncStatusText = () => {
    switch (syncStatus) {
      case 'connected': return 'Connected';
      case 'syncing': return 'Syncing...';
      case 'error': return 'Error';
      default: return 'Disconnected';
    }
  };

  // Add AI Assistant Section to Dashboard
  const renderAiAssistant = () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>AI Assistant</Text>
      <View style={styles.aiContainer}>
        {isAiGenerating ? (
          <View style={styles.aiGenerating}>
            <ActivityIndicator size={20} color="#667eea" />
            <Text style={styles.aiGeneratingText}>AI is generating content...</Text>
          </View>
        ) : (
          <>
            <TouchableOpacity
              style={styles.aiPromptButton}
              onPress={() => setShowAiModal(true)}
            >
              <Ionicons name="sparkles" size={20} color="#FFFFFF" />
              <Text style={styles.aiPromptButtonText}>Generate with AI</Text>
            </TouchableOpacity>
            
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              style={styles.aiSuggestions}
            >
              {aiSuggestions.map((suggestion) => (
                <TouchableOpacity
                  key={suggestion.id}
                  style={styles.aiSuggestionCard}
                  onPress={suggestion.action}
                >
                  <Ionicons name="bulb" size={20} color="#F59E0B" />
                  <Text style={styles.aiSuggestionTitle}>{suggestion.title}</Text>
                  <Text style={styles.aiSuggestionDesc}>{suggestion.description}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </>
        )}
      </View>
    </View>
  );

  const renderAiModal = () => (
    <Modal
      visible={showAiModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowAiModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowAiModal(false)}
          >
            <Ionicons name="close" size={24} color="#8E8E93" />
          </TouchableOpacity>
          
          <Text style={styles.modalTitle}>AI Content Generator</Text>
          
          <TouchableOpacity
            style={[styles.modalSaveButton, (!aiPrompt.trim() || isAiGenerating) && styles.disabledButton]}
            onPress={() => generateAiContent(aiPrompt, aiContentType)}
            disabled={!aiPrompt.trim() || isAiGenerating}
          >
            {isAiGenerating ? (
              <ActivityIndicator size={20} color="#FFFFFF" />
            ) : (
              <Text style={styles.modalSaveButtonText}>Generate</Text>
            )}
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalBody}>
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Content Type</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.contentTypeSelectorContent}
            >
              {contentTypes.map((type) => (
                <TouchableOpacity
                  key={type.id}
                  style={[
                    styles.contentTypeButton,
                    { backgroundColor: type.color + '20' },
                    aiContentType === type.id && { backgroundColor: type.color },
                  ]}
                  onPress={() => setAiContentType(type.id)}
                >
                  <Ionicons
                    name={type.icon}
                    size={20}
                    color={aiContentType === type.id ? '#FFFFFF' : type.color}
                  />
                  <Text
                    style={[
                      styles.contentTypeText,
                      { color: aiContentType === type.id ? '#FFFFFF' : type.color },
                    ]}
                  >
                    {type.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>AI Prompt</Text>
            <TextInput
              style={[styles.formInput, styles.largeTextArea]}
              value={aiPrompt}
              onChangeText={setAiPrompt}
              placeholder="Describe what you want to create... (e.g., 'A blog post about digital marketing trends')"
              placeholderTextColor="#C7C7CC"
              multiline
              numberOfLines={4}
            />
          </View>

          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>AI Tips</Text>
            <Text style={styles.aiTips}>
              • Be specific about your topic and target audience{'\n'}
              • Mention the tone you want (professional, casual, etc.){'\n'}
              • Include key points you want to cover{'\n'}
              • Specify the length or detail level needed{'\n'}
              • Add any specific keywords or phrases to include
            </Text>
          </View>

          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>API Status</Text>
            <View style={styles.apiStatus}>
              <View style={styles.apiStatusItem}>
                <Ionicons name="sparkles-outline" size={16} color={apiKeys.openai && apiKeys.openai !== 'your-openai-api-key-here' ? '#10B981' : '#EF4444'} />
                <Text style={styles.apiStatusText}>OpenAI: {apiKeys.openai && apiKeys.openai !== 'your-openai-api-key-here' ? 'Connected' : 'Not Configured'}</Text>
              </View>
              <View style={styles.apiStatusItem}>
                <Ionicons name="globe" size={16} color={apiKeys.openrouter && apiKeys.openrouter !== 'your-openrouter-api-key-here' ? '#10B981' : '#EF4444'} />
                <Text style={styles.apiStatusText}>OpenRouter: {apiKeys.openrouter && apiKeys.openrouter !== 'your-openrouter-api-key-here' ? 'Connected' : 'Not Configured'}</Text>
              </View>
              <View style={styles.apiStatusItem}>
                <Ionicons name="heart" size={16} color={apiKeys.huggingface && apiKeys.huggingface !== 'your-huggingface-api-key-here' ? '#10B981' : '#EF4444'} />
                <Text style={styles.apiStatusText}>Hugging Face: {apiKeys.huggingface && apiKeys.huggingface !== 'your-huggingface-api-key-here' ? 'Connected' : 'Not Configured'}</Text>
              </View>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderApiKeyModal = () => (
    <Modal
      visible={showApiKeyModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowApiKeyModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>AI API Keys Configuration</Text>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowApiKeyModal(false)}
          >
            <Ionicons name="close" size={24} color="#8E8E93" />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalBody}>
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>AI Service Providers</Text>
            
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>OpenAI API Key</Text>
              <TextInput
                style={styles.formInput}
                value={apiKeys.openai}
                onChangeText={(text) => setApiKeys({...apiKeys, openai: text})}
                placeholder="sk-..."
                secureTextEntry
              />
              <Text style={styles.formHelpText}>
                Get your key from platform.openai.com
              </Text>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>OpenRouter API Key</Text>
              <TextInput
                style={styles.formInput}
                value={apiKeys.openrouter}
                onChangeText={(text) => setApiKeys({...apiKeys, openrouter: text})}
                placeholder="sk-or-..."
                secureTextEntry
              />
              <Text style={styles.formHelpText}>
                Get your key from openrouter.ai (free tier available)
              </Text>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Hugging Face API Key</Text>
              <TextInput
                style={styles.formInput}
                value={apiKeys.huggingface}
                onChangeText={(text) => setApiKeys({...apiKeys, huggingface: text})}
                placeholder="hf_..."
                secureTextEntry
              />
              <Text style={styles.formHelpText}>
                Get your key from huggingface.co (some models are free)
              </Text>
            </View>

            <TouchableOpacity 
              style={styles.saveButton}
              onPress={saveApiKeys}
            >
              <Text style={styles.saveButtonText}>Save API Keys</Text>
            </TouchableOpacity>

            <View style={styles.apiInfo}>
              <Text style={styles.apiInfoTitle}>Important Notes:</Text>
              <Text style={styles.apiInfoText}>
                • API keys are stored locally on your device{'\n'}
                • Never share your API keys with anyone{'\n'}
                • OpenAI charges based on usage{'\n'}
                • OpenRouter offers free credits for testing{'\n'}
                • The app will try providers in order until one works
              </Text>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderHeader = () => (
    <LinearGradient
      colors={['#667eea', '#764ba2']}
      style={styles.header}
    >
      <StatusBar barStyle="light-content" />
      <SafeAreaView>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View style={styles.headerTitleContainer}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {companyLogo ? (
                <Image source={{ uri: companyLogo || undefined }} style={{ width: 28, height: 28, borderRadius: 14, marginRight: 8, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.4)' }} />
              ) : (
                <MaterialCommunityIcons name="web" size={24} color="#fff" style={{ marginRight: 8 }} />
              )}
              <Text style={styles.headerTitle}>Website Management</Text>
            </View>
            <View style={styles.syncStatus}>
              <View style={[styles.syncIndicator, { backgroundColor: getSyncStatusColor() }]} />
              <Text style={styles.syncText}>{getSyncStatusText()}</Text>
            </View>
          </View>

          <View style={styles.headerActions}>
            <TouchableOpacity 
              style={styles.headerAction} 
              onPress={() => setShowApiKeyModal(true)}
            >
              <Ionicons name="key" size={20} color="#FFFFFF" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.headerAction}
              onPress={() => {
                resetWebsiteForm();
                setShowWebsiteModal(true);
              }}
            >
              <Ionicons name="add" size={24} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );

  const renderTabBar = () => (
    <View style={styles.tabBar}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabBarContent}
      >
        {[
          { id: 'dashboard', name: 'Dashboard', icon: 'grid' },
          { id: 'websites', name: 'Websites', icon: 'globe' },
          { id: 'content', name: 'Content', icon: 'document-text' },
          { id: 'analytics', name: 'Analytics', icon: 'analytics' },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, activeTab === tab.id && styles.activeTab]}
            onPress={() => setActiveTab(tab.id)}
          >
            <Ionicons
              name={tab.icon}
              size={20}
              color={activeTab === tab.id ? '#FFFFFF' : '#8E8E93'}
            />
            <Text
              style={[
                styles.tabText,
                { color: activeTab === tab.id ? '#FFFFFF' : '#8E8E93' },
              ]}
            >
              {tab.name}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderDashboard = () => (
    <ScrollView
      style={styles.content}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
      }
      showsVerticalScrollIndicator={false}
    >
      {/* Quick Stats */}
      <View style={styles.statsContainer}>
        <Text style={styles.sectionTitle}>Overview</Text>
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <View style={styles.statIconContainer}>
              <Ionicons name="globe" size={24} color="#667eea" />
            </View>
            <Text style={styles.statValue}>{websites.length}</Text>
            <Text style={styles.statLabel}>Websites</Text>
          </View>
          
          <View style={styles.statCard}>
            <View style={styles.statIconContainer}>
              <Ionicons name="eye" size={24} color="#10B981" />
            </View>
            <Text style={styles.statValue}>{analytics.totalVisitors}</Text>
            <Text style={styles.statLabel}>Total Visitors</Text>
          </View>
          
          <View style={styles.statCard}>
            <View style={styles.statIconContainer}>
              <Ionicons name="document-text" size={24} color="#F59E0B" />
            </View>
            <Text style={styles.statValue}>
              {Object.values(websiteContent).reduce((total, items) => total + items.length, 0)}
            </Text>
            <Text style={styles.statLabel}>Content Items</Text>
          </View>
          
          <View style={styles.statCard}>
            <View style={styles.statIconContainer}>
              <Ionicons name="checkmark-circle" size={24} color="#EF4444" />
            </View>
            <Text style={styles.statValue}>
              {websites.filter(w => w.isPublished).length}
            </Text>
            <Text style={styles.statLabel}>Published</Text>
          </View>
        </View>
      </View>

      {/* AI Assistant Section */}
      {renderAiAssistant()}

      {/* Recent Websites */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Recent Websites</Text>
          <TouchableOpacity onPress={() => setActiveTab('websites')}>
            <Text style={styles.sectionAction}>View All</Text>
          </TouchableOpacity>
        </View>
        
        {websites.slice(0, 3).map((website) => (
          <TouchableOpacity
            key={website.id}
            style={styles.websiteCard}
            onPress={() => setSelectedWebsite(website)}
          >
            <View style={styles.websiteCardContent}>
              <View style={styles.websiteInfo}>
                <Text style={styles.websiteName}>{website.name}</Text>
                <Text style={styles.websiteDomain}>{website.domain}</Text>
                <View style={styles.websiteStatus}>
                  <View style={[
                    styles.statusIndicator,
                    { backgroundColor: website.isPublished ? '#10B981' : '#F59E0B' }
                  ]} />
                  <Text style={styles.statusText}>
                    <Text>{website.isPublished ? 'Published' : 'Draft'}</Text>
                  </Text>
                </View>
              </View>
              
              <View style={styles.websiteActions}>
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => previewWebsite(website)}
                >
                  <Ionicons name="eye" size={16} color="#8B5CF6" />
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => editWebsite(website)}
                >
                  <Ionicons name="create" size={16} color="#667eea" />
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={() => shareWebsite(website)}
                >
                  <Ionicons name="share" size={16} color="#10B981" />
                </TouchableOpacity>
                
                {website.isPublished && (
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() => openWebsite(website)}
                  >
                    <Ionicons name="open" size={16} color="#F59E0B" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </TouchableOpacity>
        ))}
      </View>

      {/* Quick Actions */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Quick Actions</Text>
        <View style={styles.quickActions}>
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => {
              resetWebsiteForm();
              setShowWebsiteModal(true);
            }}
          >
            <View style={[styles.quickActionIcon, { backgroundColor: '#667eea20' }]}>
              <Ionicons name="add" size={24} color="#667eea" />
            </View>
            <Text style={styles.quickActionText}>Create Website</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => setShowAiModal(true)}
          >
            <View style={[styles.quickActionIcon, { backgroundColor: '#8B5CF620' }]}>
              <Ionicons name="sparkles" size={24} color="#8B5CF6" />
            </View>
            <Text style={styles.quickActionText}>AI Assistant</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => {
              resetContentForm();
              setShowContentModal(true);
            }}
          >
            <View style={[styles.quickActionIcon, { backgroundColor: '#F59E0B20' }]}>
              <Ionicons name="document-text" size={24} color="#F59E0B" />
            </View>
            <Text style={styles.quickActionText}>Add Content</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.quickAction}
            onPress={() => {
              if (navigation && navigation.navigate) {
                navigation.navigate('WebsiteAnalyticsDashboard', {
                  websiteId: selectedWebsite?.id || null,
                  websiteName: selectedWebsite?.name || companyProfile?.company_name || companyProfile?.name || 'Website',
                  companyProfile,
                  companyId,
                });
              } else {
                setActiveTab('analytics');
              }
            }}
          >
            <View style={[styles.quickActionIcon, { backgroundColor: '#EF444420' }]}>
              <Ionicons name="analytics" size={24} color="#EF4444" />
            </View>
            <Text style={styles.quickActionText}>View Analytics</Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );

  const renderWebsites = () => (
    <ScrollView
      style={styles.content}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
      }
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>All Websites ({websites.length})</Text>
          <TouchableOpacity
            style={styles.addButton}
            onPress={() => {
              resetWebsiteForm();
              setShowWebsiteModal(true);
            }}
          >
            <Ionicons name="add" size={16} color="#FFFFFF" />
            <Text style={styles.addButtonText}>New Website</Text>
          </TouchableOpacity>
        </View>
        
        {websites.map((website) => (
          <View key={website.id} style={styles.websiteCard}>
            <View style={styles.websiteCardHeader}>
              <View style={styles.websiteInfo}>
                <Text style={styles.websiteName}>{website.name}</Text>
                <Text style={styles.websiteDomain}>{website.domain}</Text>
                <View style={styles.websiteMeta}>
                  <View style={styles.websiteStatus}>
                    <View style={[
                      styles.statusIndicator,
                      { backgroundColor: website.isPublished ? '#10B981' : '#F59E0B' }
                    ]} />
                    <Text style={styles.statusText}>
                      <Text>{website.isPublished ? 'Published' : 'Draft'}</Text>
                    </Text>
                  </View>
                  
                  <Text style={styles.websiteDate}>
                    Updated {new Date(website.updatedAt).toLocaleDateString()}
                  </Text>
                </View>
              </View>
              
              <TouchableOpacity
                style={styles.websiteMenu}
                onPress={() => enhancedWebsiteActions(website)}
              >
                <Ionicons name="ellipsis-vertical" size={20} color="#8E8E93" />
              </TouchableOpacity>
            </View>
            
            {website.stats && (
              <View style={styles.websiteStats}>
                <View style={styles.websiteStat}>
                  <Ionicons name="eye" size={14} color="#8E8E93" />
                  <Text style={styles.websiteStatText}>{website.stats.visitors} visitors</Text>
                </View>
                
                <View style={styles.websiteStat}>
                  <Ionicons name="document-text" size={14} color="#8E8E93" />
                  <Text style={styles.websiteStatText}>{website.stats.pageViews} views</Text>
                </View>
                
                <View style={styles.websiteStat}>
                  <Ionicons name="time" size={14} color="#8E8E93" />
                  <Text style={styles.websiteStatText}>{website.stats.avgSessionDuration}s avg</Text>
                </View>
              </View>
            )}
            
            <View style={styles.websiteActions}>
              <TouchableOpacity
                style={[styles.websiteActionButton, styles.previewButton]}
                onPress={() => previewWebsite(website)}
              >
                <Ionicons name="eye" size={16} color="#8B5CF6" />
                <Text style={[styles.websiteActionText, { color: '#8B5CF6' }]}>Preview</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.websiteActionButton, styles.editButton]}
                onPress={() => editWebsite(website)}
              >
                <Ionicons name="create" size={16} color="#667eea" />
                <Text style={[styles.websiteActionText, { color: '#667eea' }]}>Edit</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.websiteActionButton, styles.shareButton]}
                onPress={() => shareWebsite(website)}
              >
                <Ionicons name="share" size={16} color="#10B981" />
                <Text style={[styles.websiteActionText, { color: '#10B981' }]}>Share</Text>
              </TouchableOpacity>
              
              {website.isPublished ? (
                <TouchableOpacity
                  style={[styles.websiteActionButton, styles.openButton]}
                  onPress={() => openWebsite(website)}
                >
                  <Ionicons name="open" size={16} color="#F59E0B" />
                  <Text style={[styles.websiteActionText, { color: '#F59E0B' }]}>Open</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[styles.websiteActionButton, styles.publishButton]}
                  onPress={() => publishWebsite(website.id)}
                >
                  <Ionicons name="cloud-upload" size={16} color="#FFFFFF" />
                  <Text style={[styles.websiteActionText, { color: '#FFFFFF' }]}>Publish</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        ))}
        
        {websites.length === 0 && (
          <View style={styles.emptyState}>
            <Ionicons name="globe-outline" size={64} color="#C7C7CC" />
            <Text style={styles.emptyStateTitle}>No Websites Yet</Text>
            <Text style={styles.emptyStateText}>
              Create your first website to get started with your online presence
            </Text>
            <TouchableOpacity
              style={styles.emptyStateButton}
              onPress={() => {
                resetWebsiteForm();
                setShowWebsiteModal(true);
              }}
            >
              <Text style={styles.emptyStateButtonText}>Create Website</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </ScrollView>
  );

  const renderContent = () => (
    <ScrollView
      style={styles.content}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
      }
      showsVerticalScrollIndicator={false}
    >
      {/* AI Quick Action */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.aiContentButton}
          onPress={() => setShowAiModal(true)}
        >
          <Ionicons name="sparkles" size={20} color="#FFFFFF" />
          <Text style={styles.aiContentButtonText}>Generate Content with AI</Text>
        </TouchableOpacity>
      </View>

      {/* Content Type Selector */}
      <View style={styles.contentTypeSelector}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.contentTypeSelectorContent}
        >
          {contentTypes.map((type) => (
            <TouchableOpacity
              key={type.id}
              style={[
                styles.contentTypeButton,
                { backgroundColor: type.color + '20' },
                contentType === type.id && { backgroundColor: type.color },
              ]}
              onPress={() => setContentType(type.id)}
            >
              <Ionicons
                name={type.icon}
                size={20}
                color={contentType === type.id ? '#FFFFFF' : type.color}
              />
              <Text
                style={[
                  styles.contentTypeText,
                  { color: contentType === type.id ? '#FFFFFF' : type.color },
                ]}
              >
                {type.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>
            {contentTypes.find(t => t.id === contentType)?.name} ({websiteContent[contentType]?.length || 0})
          </Text>
          <TouchableOpacity
            style={styles.addButton}
            onPress={() => {
              resetContentForm();
              setShowContentModal(true);
            }}
          >
            <Ionicons name="add" size={16} color="#FFFFFF" />
            <Text style={styles.addButtonText}>Add {contentTypes.find(t => t.id === contentType)?.name}</Text>
          </TouchableOpacity>
        </View>
        
        {websiteContent[contentType]?.map((content) => (
          <View key={content.id} style={styles.contentCard}>
            <View style={styles.contentCardHeader}>
              <View style={styles.contentInfo}>
                <Text style={styles.contentTitle}>{content.title}</Text>
                <Text style={styles.contentExcerpt} numberOfLines={2}>
                  {content.excerpt || content.content}
                </Text>
                <View style={styles.contentMeta}>
                  <View style={styles.contentStatus}>
                    <View style={[
                      styles.statusIndicator,
                      { backgroundColor: content.isPublished ? '#10B981' : '#F59E0B' }
                    ]} />
                    <Text style={styles.statusText}>
                      <Text>{content.isPublished ? 'Published' : 'Draft'}</Text>
                    </Text>
                  </View>
                  
                  <Text style={styles.contentDate}>
                    {new Date(content.publishDate || content.createdAt).toLocaleDateString()}
                  </Text>
                </View>
              </View>
              
              <TouchableOpacity
                style={styles.contentMenu}
                onPress={() => enhancedContentActions(content)}
              >
                <Ionicons name="ellipsis-vertical" size={20} color="#8E8E93" />
              </TouchableOpacity>
            </View>
            
            <View style={styles.contentActions}>
              <TouchableOpacity
                style={[styles.contentActionButton, styles.previewButton]}
                onPress={() => previewContentItem(content)}
              >
                <Ionicons name="eye" size={16} color="#8B5CF6" />
                <Text style={[styles.contentActionText, { color: '#8B5CF6' }]}>Preview</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.contentActionButton, styles.editButton]}
                onPress={() => editContent(content)}
              >
                <Ionicons name="create" size={16} color="#667eea" />
                <Text style={[styles.contentActionText, { color: '#667eea' }]}>Edit</Text>
              </TouchableOpacity>
              
              {content.isPublished ? (
                <TouchableOpacity
                  style={[styles.contentActionButton, styles.unpublishButton]}
                  onPress={() => {
                    // Toggle publish status
                    const updatedContent = { ...websiteContent };
                    updatedContent[contentType] = updatedContent[contentType].map(c =>
                      c.id === content.id ? { ...c, isPublished: false } : c
                    );
                    setWebsiteContent(updatedContent);
                  }}
                >
                  <Ionicons name="eye-off" size={16} color="#F59E0B" />
                  <Text style={[styles.contentActionText, { color: '#F59E0B' }]}>Unpublish</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[styles.contentActionButton, styles.publishButton]}
                  onPress={() => {
                    // Toggle publish status
                    const updatedContent = { ...websiteContent };
                    updatedContent[contentType] = updatedContent[contentType].map(c =>
                      c.id === content.id ? { ...c, isPublished: true } : c
                    );
                    setWebsiteContent(updatedContent);
                  }}
                >
                  <Ionicons name="cloud-upload" size={16} color="#FFFFFF" />
                  <Text style={[styles.contentActionText, { color: '#FFFFFF' }]}>Publish</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )) || []}
        
        {(!websiteContent[contentType] || websiteContent[contentType].length === 0) && (
          <View style={styles.emptyState}>
            <Ionicons 
              name={contentTypes.find(t => t.id === contentType)?.icon + '-outline'} 
              size={64} 
              color="#C7C7CC" 
            />
            <Text style={styles.emptyStateTitle}>
              No {contentTypes.find(t => t.id === contentType)?.name} Yet
            </Text>
            <Text style={styles.emptyStateText}>
              Create your first {contentTypes.find(t => t.id === contentType)?.name.toLowerCase()} to get started
            </Text>
            <TouchableOpacity
              style={styles.emptyStateButton}
              onPress={() => {
                resetContentForm();
                setShowContentModal(true);
              }}
            >
              <Text style={styles.emptyStateButtonText}>
                Add {contentTypes.find(t => t.id === contentType)?.name}
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </ScrollView>
  );

  const renderAnalytics = () => (
    <ScrollView
      style={styles.content}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
      }
      showsVerticalScrollIndicator={false}
    >
      {/* Navigate to Full Analytics Dashboard */}
      <View style={styles.section}>
        <TouchableOpacity
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#667eea',
            paddingVertical: 14,
            borderRadius: 12,
            gap: 8,
          }}
          onPress={() => {
            if (navigation && navigation.navigate) {
              navigation.navigate('WebsiteAnalyticsDashboard', {
                websiteId: selectedWebsite?.id || null,
                websiteName: selectedWebsite?.name || companyProfile?.company_name || companyProfile?.name || 'Website',
                companyProfile,
                companyId,
              });
            }
          }}
        >
          <Ionicons name="analytics" size={20} color="#FFFFFF" />
          <Text style={{ fontSize: 16, fontWeight: '600', color: '#FFFFFF' }}>
            Open Full Analytics Dashboard
          </Text>
          <Ionicons name="arrow-forward" size={18} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      {/* Analytics Overview */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Analytics Overview</Text>
        <View style={styles.analyticsGrid}>
          <View style={styles.analyticsCard}>
            <View style={styles.analyticsCardHeader}>
              <Ionicons name="eye" size={24} color="#667eea" />
              <Text style={styles.analyticsValue}>{analytics.totalVisitors}</Text>
            </View>
            <Text style={styles.analyticsLabel}>Total Visitors</Text>
            <Text style={styles.analyticsChange}>+12% from last month</Text>
          </View>
          
          <View style={styles.analyticsCard}>
            <View style={styles.analyticsCardHeader}>
              <Ionicons name="document-text" size={24} color="#10B981" />
              <Text style={styles.analyticsValue}>{analytics.pageViews}</Text>
            </View>
            <Text style={styles.analyticsLabel}>Page Views</Text>
            <Text style={styles.analyticsChange}>+8% from last month</Text>
          </View>
          
          <View style={styles.analyticsCard}>
            <View style={styles.analyticsCardHeader}>
              <Ionicons name="trending-down" size={24} color="#F59E0B" />
              <Text style={styles.analyticsValue}>{analytics.bounceRate}%</Text>
            </View>
            <Text style={styles.analyticsLabel}>Bounce Rate</Text>
            <Text style={styles.analyticsChange}>-3% from last month</Text>
          </View>
          
          <View style={styles.analyticsCard}>
            <View style={styles.analyticsCardHeader}>
              <Ionicons name="time" size={24} color="#EF4444" />
              <Text style={styles.analyticsValue}>{Math.floor(analytics.avgSessionDuration / 60)}m</Text>
            </View>
            <Text style={styles.analyticsLabel}>Avg. Session</Text>
            <Text style={styles.analyticsChange}>+15% from last month</Text>
          </View>
        </View>
      </View>

      {/* Top Pages */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Top Pages</Text>
        {analytics.topPages?.map((page, index) => (
          <View key={index} style={styles.pageCard}>
            <View style={styles.pageInfo}>
              <Text style={styles.pageTitle}>{page.title}</Text>
              <Text style={styles.pagePath}>{page.path}</Text>
            </View>
            <View style={styles.pageStats}>
              <Text style={styles.pageViews}>{page.views}</Text>
              <Text style={styles.pageViewsLabel}>views</Text>
            </View>
          </View>
        ))}
      </View>

      {/* Recent Activity */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Recent Activity</Text>
        {analytics.recentActivity?.map((activity, index) => (
          <View key={index} style={styles.activityCard}>
            <View style={styles.activityIcon}>
              <Ionicons 
                name={activity.action === 'Page View' ? 'eye' : 'mail'} 
                size={16} 
                color="#667eea" 
              />
            </View>
            <View style={styles.activityInfo}>
              <Text style={styles.activityAction}>{activity.action}</Text>
              <Text style={styles.activityPage}>{activity.page}</Text>
            </View>
            <Text style={styles.activityTime}>{activity.time}</Text>
          </View>
        ))}
      </View>
    </ScrollView>
  );

  const renderWebsiteModal = () => (
    <Modal
      visible={showWebsiteModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowWebsiteModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <KeyboardAvoidingView 
          style={styles.modalContent}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          {/* Modal Header */}
          <View style={styles.modalHeader}>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setShowWebsiteModal(false)}
            >
              <Ionicons name="close" size={24} color="#8E8E93" />
            </TouchableOpacity>
            
            <Text style={styles.modalTitle}>
              <Text>{websiteForm.id ? 'Edit Website' : 'Create Website'}</Text>
            </Text>
            
            <TouchableOpacity
              style={styles.modalSaveButton}
              onPress={saveWebsite}
            >
              <Text style={styles.modalSaveButtonText}>Save</Text>
            </TouchableOpacity>
          </View>

          {/* Modal Content */}
          <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
            <View style={styles.formSection}>
              <Text style={styles.formSectionTitle}>Basic Information</Text>
              
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Website Name *</Text>
                <TextInput
                  style={styles.formInput}
                  value={websiteForm.name}
                  onChangeText={(text) => setWebsiteForm({...websiteForm, name: text})}
                  placeholder="My Business Website"
                  placeholderTextColor="#C7C7CC"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Domain *</Text>
                <TextInput
                  style={styles.formInput}
                  value={websiteForm.domain}
                  onChangeText={(text) => setWebsiteForm({...websiteForm, domain: text})}
                  placeholder="mybusiness.com"
                  placeholderTextColor="#C7C7CC"
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Website Title *</Text>
                <TextInput
                  style={styles.formInput}
                  value={websiteForm.title}
                  onChangeText={(text) => setWebsiteForm({...websiteForm, title: text})}
                  placeholder="Welcome to My Business"
                  placeholderTextColor="#C7C7CC"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Description</Text>
                <TextInput
                  style={[styles.formInput, styles.textArea]}
                  value={websiteForm.description}
                  onChangeText={(text) => setWebsiteForm({...websiteForm, description: text})}
                  placeholder="Brief description of your website"
                  placeholderTextColor="#C7C7CC"
                  multiline
                  numberOfLines={3}
                />
              </View>
            </View>

            <View style={styles.formSection}>
              <Text style={styles.formSectionTitle}>Template Selection</Text>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.templatesContainer}
              >
                {websiteTemplates.map((template) => (
                  <TouchableOpacity
                    key={template.id}
                    style={[
                      styles.templateCard,
                      websiteForm.template === template.id && styles.selectedTemplate,
                    ]}
                    onPress={() => setWebsiteForm({...websiteForm, template: template.id})}
                  >
                    <View style={[styles.templateImage, { backgroundColor: template.color + '20' }]}>
                      <Ionicons name="globe" size={32} color={template.color} />
                    </View>
                    <Text style={styles.templateName}>{template.name}</Text>
                    <Text style={styles.templateDescription} numberOfLines={2}>
                      {template.description}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            <View style={styles.formSection}>
              <Text style={styles.formSectionTitle}>Contact Information</Text>
              
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Email</Text>
                <TextInput
                  style={styles.formInput}
                  value={websiteForm.contact.email}
                  onChangeText={(text) => setWebsiteForm({
                    ...websiteForm,
                    contact: {...websiteForm.contact, email: text}
                  })}
                  placeholder="contact@mybusiness.com"
                  placeholderTextColor="#C7C7CC"
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Phone</Text>
                <TextInput
                  style={styles.formInput}
                  value={websiteForm.contact.phone}
                  onChangeText={(text) => setWebsiteForm({
                    ...websiteForm,
                    contact: {...websiteForm.contact, phone: text}
                  })}
                  placeholder="+44 20 1234 5678"
                  placeholderTextColor="#C7C7CC"
                  keyboardType="phone-pad"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Address</Text>
                <TextInput
                  style={[styles.formInput, styles.textArea]}
                  value={websiteForm.contact.address}
                  onChangeText={(text) => setWebsiteForm({
                    ...websiteForm,
                    contact: {...websiteForm.contact, address: text}
                  })}
                  placeholder="123 Business Street, London, UK"
                  placeholderTextColor="#C7C7CC"
                  multiline
                  numberOfLines={2}
                />
              </View>
            </View>

            <View style={styles.formSection}>
              <Text style={styles.formSectionTitle}>Features</Text>
              
              {Object.entries(websiteForm.features).map(([key, value]) => (
                <View key={key} style={styles.featureItem}>
                  <View style={styles.featureInfo}>
                    <Text style={styles.featureLabel}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </Text>
                    <Text style={styles.featureDescription}>
                      Enable {key} functionality on your website
                    </Text>
                  </View>
                  <Switch
                    value={value}
                    onValueChange={(newValue) => setWebsiteForm({
                      ...websiteForm,
                      features: {...websiteForm.features, [key]: newValue}
                    })}
                    trackColor={{ false: '#E5E5EA', true: '#667eea' }}
                    thumbColor="#FFFFFF"
                  />
                </View>
              ))}
            </View>

            <View style={styles.formSection}>
              <Text style={styles.formSectionTitle}>SEO Settings</Text>
              
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Meta Title</Text>
                <TextInput
                  style={styles.formInput}
                  value={websiteForm.seo.metaTitle}
                  onChangeText={(text) => setWebsiteForm({
                    ...websiteForm,
                    seo: {...websiteForm.seo, metaTitle: text}
                  })}
                  placeholder="SEO title for search engines"
                  placeholderTextColor="#C7C7CC"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Meta Description</Text>
                <TextInput
                  style={[styles.formInput, styles.textArea]}
                  value={websiteForm.seo.metaDescription}
                  onChangeText={(text) => setWebsiteForm({
                    ...websiteForm,
                    seo: {...websiteForm.seo, metaDescription: text}
                  })}
                  placeholder="SEO description for search engines"
                  placeholderTextColor="#C7C7CC"
                  multiline
                  numberOfLines={2}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Keywords</Text>
                <TextInput
                  style={styles.formInput}
                  value={websiteForm.seo.keywords}
                  onChangeText={(text) => setWebsiteForm({
                    ...websiteForm,
                    seo: {...websiteForm.seo, keywords: text}
                  })}
                  placeholder="keyword1, keyword2, keyword3"
                  placeholderTextColor="#C7C7CC"
                />
              </View>

              <TouchableOpacity
                style={styles.aiSeoButton}
                onPress={generateSeoOptimization}
              >
                <Ionicons name="sparkles" size={16} color="#FFFFFF" />
                <Text style={styles.aiSeoButtonText}>Generate SEO with AI</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  const renderContentModal = () => (
    <Modal
      visible={showContentModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowContentModal(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <KeyboardAvoidingView 
          style={styles.modalContent}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          {/* Modal Header */}
          <View style={styles.modalHeader}>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setShowContentModal(false)}
            >
              <Ionicons name="close" size={24} color="#8E8E93" />
            </TouchableOpacity>
            
            <Text style={styles.modalTitle}>
              <Text>{editingContent ? 'Edit' : 'Create'} {contentTypes.find(t => t.id === contentType)?.name}</Text>
            </Text>
            
            <TouchableOpacity
              style={styles.modalSaveButton}
              onPress={saveContent}
            >
              <Text style={styles.modalSaveButtonText}>Save</Text>
            </TouchableOpacity>
          </View>

          {/* Modal Content */}
          <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
            <View style={styles.formSection}>
              <Text style={styles.formSectionTitle}>Content Information</Text>
              
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Title *</Text>
                <TextInput
                  style={styles.formInput}
                  value={contentForm.title}
                  onChangeText={(text) => setContentForm({...contentForm, title: text})}
                  placeholder="Enter content title"
                  placeholderTextColor="#C7C7CC"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Content *</Text>
                <TextInput
                  style={[styles.formInput, styles.largeTextArea]}
                  value={contentForm.content}
                  onChangeText={(text) => setContentForm({...contentForm, content: text})}
                  placeholder="Write your content here..."
                  placeholderTextColor="#C7C7CC"
                  multiline
                  numberOfLines={8}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Excerpt</Text>
                <TextInput
                  style={[styles.formInput, styles.textArea]}
                  value={contentForm.excerpt}
                  onChangeText={(text) => setContentForm({...contentForm, excerpt: text})}
                  placeholder="Brief summary or excerpt"
                  placeholderTextColor="#C7C7CC"
                  multiline
                  numberOfLines={3}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Author</Text>
                <TextInput
                  style={styles.formInput}
                  value={contentForm.author}
                  onChangeText={(text) => setContentForm({...contentForm, author: text})}
                  placeholder="Author name"
                  placeholderTextColor="#C7C7CC"
                />
              </View>
            </View>

            <View style={styles.formSection}>
              <Text style={styles.formSectionTitle}>Publishing Options</Text>
              
              <View style={styles.featureItem}>
                <View style={styles.featureInfo}>
                  <Text style={styles.featureLabel}>Published</Text>
                  <Text style={styles.featureDescription}>Make this content visible on your website</Text>
                </View>
                <Switch
                  value={contentForm.isPublished}
                  onValueChange={(value) => setContentForm({...contentForm, isPublished: value})}
                  trackColor={{ false: '#E5E5EA', true: '#667eea' }}
                  thumbColor="#FFFFFF"
                />
              </View>

              <View style={styles.featureItem}>
                <View style={styles.featureInfo}>
                  <Text style={styles.featureLabel}>Featured</Text>
                  <Text style={styles.featureDescription}>Highlight this content on your homepage</Text>
                </View>
                <Switch
                  value={contentForm.isFeatured}
                  onValueChange={(value) => setContentForm({...contentForm, isFeatured: value})}
                  trackColor={{ false: '#E5E5EA', true: '#667eea' }}
                  thumbColor="#FFFFFF"
                />
              </View>
            </View>

            <View style={styles.formSection}>
              <Text style={styles.formSectionTitle}>SEO Settings</Text>
              
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Meta Title</Text>
                <TextInput
                  style={styles.formInput}
                  value={contentForm.seo.metaTitle}
                  onChangeText={(text) => setContentForm({
                    ...contentForm,
                    seo: {...contentForm.seo, metaTitle: text}
                  })}
                  placeholder="SEO title for search engines"
                  placeholderTextColor="#C7C7CC"
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Meta Description</Text>
                <TextInput
                  style={[styles.formInput, styles.textArea]}
                  value={contentForm.seo.metaDescription}
                  onChangeText={(text) => setContentForm({
                    ...contentForm,
                    seo: {...contentForm.seo, metaDescription: text}
                  })}
                  placeholder="SEO description for search engines"
                  placeholderTextColor="#C7C7CC"
                  multiline
                  numberOfLines={2}
                />
              </View>

              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Keywords</Text>
                <TextInput
                  style={styles.formInput}
                  value={contentForm.seo.keywords}
                  onChangeText={(text) => setContentForm({
                    ...contentForm,
                    seo: {...contentForm.seo, keywords: text}
                  })}
                  placeholder="keyword1, keyword2, keyword3"
                  placeholderTextColor="#C7C7CC"
                />
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#667eea" />
        <Text style={styles.loadingText}>Loading website data...</Text>
      </View>
    );
  }

  return (
    <Animated.View 
      style={[
        styles.container,
        {
          opacity: fadeAnim,
          transform: [
            { translateY: slideAnim },
            { scale: scaleAnim },
          ],
        },
      ]}
    >
      {renderHeader()}
      {renderTabBar()}
      
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'websites' && renderWebsites()}
      {activeTab === 'content' && renderContent()}
      {activeTab === 'analytics' && renderAnalytics()}
      
      {renderWebsiteModal()}
      {renderContentModal()}
      {renderAiModal()}
      {renderApiKeyModal()}
      {renderPreviewModal()}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8F9FA',
  },
  loadingText: {
    fontSize: 16,
    color: '#8E8E93',
    marginTop: 16,
  },
  header: {
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 10,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  syncStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  syncIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  syncText: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  headerAction: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabBar: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  tabBarContent: {
    paddingHorizontal: 20,
    gap: 4,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 6,
  },
  activeTab: {
    backgroundColor: '#667eea',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  section: {
    padding: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  sectionAction: {
    fontSize: 16,
    color: '#667eea',
    fontWeight: '600',
  },
  statsContainer: {
    padding: 20,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  statCard: {
    flex: 1,
    minWidth: (width - 64) / 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  statIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#F8F9FA',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#8E8E93',
    textAlign: 'center',
  },
  aiContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  aiPromptButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#8B5CF6',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    gap: 8,
    marginBottom: 12,
  },
  aiPromptButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  aiGenerating: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    gap: 8,
  },
  aiGeneratingText: {
    fontSize: 14,
    color: '#8E8E93',
  },
  aiSuggestions: {
    flexDirection: 'row',
  },
  aiSuggestionCard: {
    width: 140,
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
    padding: 12,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  aiSuggestionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1C1C1E',
    marginTop: 4,
    marginBottom: 2,
  },
  aiSuggestionDesc: {
    fontSize: 10,
    color: '#8E8E93',
    lineHeight: 12,
  },
  aiContentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#8B5CF6',
    paddingVertical: 12,
    borderRadius: 12,
    gap: 8,
  },
  aiContentButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  aiTips: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
    fontStyle: 'italic',
  },
  apiStatus: {
    gap: 8,
  },
  apiStatusItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  apiStatusText: {
    fontSize: 14,
    color: '#8E8E93',
  },
  websiteCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  websiteCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  websiteCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  websiteInfo: {
    flex: 1,
  },
  websiteName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  websiteDomain: {
    fontSize: 14,
    color: '#667eea',
    marginBottom: 8,
  },
  websiteMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  websiteStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  statusIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#8E8E93',
  },
  websiteDate: {
    fontSize: 12,
    color: '#C7C7CC',
  },
  websiteMenu: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  websiteStats: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 12,
  },
  websiteStat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  websiteStatText: {
    fontSize: 12,
    color: '#8E8E93',
  },
  websiteActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  websiteActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 4,
  },
  editButton: {
    backgroundColor: '#667eea20',
  },
  shareButton: {
    backgroundColor: '#10B98120',
  },
  openButton: {
    backgroundColor: '#F59E0B20',
  },
  previewButton: {
    backgroundColor: '#8B5CF620',
  },
  publishButton: {
    backgroundColor: '#667eea',
  },
  websiteActionText: {
    fontSize: 12,
    fontWeight: '600',
  },
  quickActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  quickAction: {
    flex: 1,
    minWidth: (width - 64) / 2,
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  quickActionIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  quickActionText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1C1C1E',
    textAlign: 'center',
  },
  contentTypeSelector: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  contentTypeSelectorContent: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    gap: 8,
  },
  contentTypeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 6,
  },
  contentTypeText: {
    fontSize: 14,
    fontWeight: '600',
  },
  contentCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  contentCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  contentInfo: {
    flex: 1,
  },
  contentTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  contentExcerpt: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
    marginBottom: 8,
  },
  contentMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  contentStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  contentDate: {
    fontSize: 12,
    color: '#C7C7CC',
  },
  contentMenu: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentActions: {
    flexDirection: 'row',
    gap: 8,
  },
  contentActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 4,
  },
  contentActionText: {
    fontSize: 12,
    fontWeight: '600',
  },
  unpublishButton: {
    backgroundColor: '#F59E0B20',
  },
  analyticsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  analyticsCard: {
    flex: 1,
    minWidth: (width - 64) / 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  analyticsCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  analyticsValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  analyticsLabel: {
    fontSize: 12,
    color: '#8E8E93',
    marginBottom: 4,
  },
  analyticsChange: {
    fontSize: 11,
    color: '#10B981',
    fontWeight: '600',
  },
  pageCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  pageInfo: {
    flex: 1,
  },
  pageTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  pagePath: {
    fontSize: 12,
    color: '#8E8E93',
  },
  pageStats: {
    alignItems: 'flex-end',
  },
  pageViews: {
    fontSize: 18,
    fontWeight: '700',
    color: '#667eea',
  },
  pageViewsLabel: {
    fontSize: 10,
    color: '#8E8E93',
  },
  activityCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#667eea20',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  activityInfo: {
    flex: 1,
  },
  activityAction: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  activityPage: {
    fontSize: 12,
    color: '#8E8E93',
  },
  activityTime: {
    fontSize: 11,
    color: '#C7C7CC',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 4,
  },
  addButtonText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1C1C1E',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#8E8E93',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
    paddingHorizontal: 40,
  },
  emptyStateButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
  },
  emptyStateButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  modalContent: {
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  modalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F2F2F7',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  modalSaveButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#667eea',
    borderRadius: 8,
  },
  modalSaveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  disabledButton: {
    backgroundColor: '#C7C7CC',
  },
  modalBody: {
    flex: 1,
  },
  formSection: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  formSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  formGroup: {
    marginBottom: 16,
  },
  formLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#1C1C1E',
    backgroundColor: '#FFFFFF',
  },
  formHelpText: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 4,
    fontStyle: 'italic',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  largeTextArea: {
    height: 120,
    textAlignVertical: 'top',
  },
  templatesContainer: {
    paddingHorizontal: 20,
    gap: 12,
  },
  templateCard: {
    width: 140,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 12,
    borderWidth: 2,
    borderColor: '#E5E5EA',
  },
  selectedTemplate: {
    borderColor: '#667eea',
  },
  templateImage: {
    height: 80,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  templateName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  templateDescription: {
    fontSize: 11,
    color: '#8E8E93',
    lineHeight: 14,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F7',
  },
  featureInfo: {
    flex: 1,
    marginRight: 12,
  },
  featureLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  featureDescription: {
    fontSize: 12,
    color: '#8E8E93',
    lineHeight: 16,
  },
  aiSeoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#8B5CF6',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
    marginTop: 8,
  },
  aiSeoButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  saveButton: {
    backgroundColor: '#667eea',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  apiInfo: {
    backgroundColor: '#F8F9FA',
    padding: 16,
    borderRadius: 8,
    marginTop: 16,
  },
  apiInfoTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  apiInfoText: {
    fontSize: 12,
    color: '#8E8E93',
    lineHeight: 16,
  },
  // Preview Styles
  previewContent: {
    padding: 20,
  },
  previewHeaderCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  previewTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1C1C1E',
    marginTop: 12,
    textAlign: 'center',
  },
  previewSubtitle: {
    fontSize: 16,
    color: '#8E8E93',
    marginTop: 4,
    textAlign: 'center',
  },
  previewDetails: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  previewSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 16,
  },
  previewDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F7',
  },
  previewDetailLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    flex: 1,
  },
  previewDetailValue: {
    fontSize: 16,
    color: '#8E8E93',
    flex: 2,
    textAlign: 'right',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  contentPreview: {
    marginTop: 16,
  },
  contentPreviewText: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
  },
  previewActions: {
    flexDirection: 'row',
    gap: 12,
  },
  previewActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: '#F8F9FA',
    gap: 8,
  },
  previewActionText: {
    fontSize: 14,
    fontWeight: '600',
  },
});